import type { CartLine } from "@/lib/api-client";
import { Badge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";
import { formatXOF } from "@/lib/format";

export interface CartLineItemProps {
  item: CartLine;
  onSetQuantity: (variantId: string, quantity: number) => void;
  onRemove: (variantId: string) => void;
  /**
   * `true` pendant N'IMPORTE QUELLE mutation panier en cours (pas seulement
   * celle de cette ligne) : simplification assumée — désactive tous les
   * boutons quantité/retrait pendant qu'une requête panier est en vol, plutôt
   * que de suivre un état "en cours" par ligne. Évite les doubles clics/
   * requêtes concurrentes qui écraseraient le résultat les unes des autres.
   */
  isMutating: boolean;
}

/**
 * Ligne de panier (VVS_MODELE_INTERFACE.md §7.2). Porté depuis
 * apps/web/components/cart/CartLineItem.tsx : les boutons [−]/[+]/✕
 * postaient vers des Server Actions liées (`formAction={action.bind(null, …)}`)
 * — remplacés par de simples `onClick` appelant les callbacks fournis par
 * `PanierPage` (elle-même branchée sur `useSetCartItemQuantity()`/
 * `useRemoveCartItem()`, cf. src/lib/cart.ts).
 *
 * « Une ligne devenue indisponible est signalée et isolée, jamais supprimée
 * en silence » (§7.2) : `stock.inStock === false` bascule la ligne dans un
 * état d'alerte dédié, avec pour seule action possible de la retirer.
 */
export function CartLineItem({ item, onSetQuantity, onRemove, isMutating }: CartLineItemProps) {
  const unavailable = !item.stock.inStock;
  const atStockLimit = item.quantity >= item.stock.quantityAvailable;

  return (
    <Card
      className={[
        "flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:gap-4",
        unavailable ? "border-gold bg-gold/5" : "",
      ].join(" ")}
    >
      <div
        aria-hidden="true"
        className="flex h-16 w-16 shrink-0 items-center justify-center rounded-sm border border-line bg-cream2 text-[10px] uppercase tracking-wide text-muted"
      >
        Visuel
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-ink">{item.productName}</p>
        {item.variantName ? <p className="text-xs text-muted">{item.variantName}</p> : null}
        <p className="mt-1 text-xs text-muted">Réf. {item.sku}</p>

        {unavailable ? (
          <p className="mt-2 flex items-center gap-1 text-xs font-medium text-red-700">
            <span aria-hidden="true">⚠</span> Cet article n&apos;est plus disponible — merci de le retirer.
          </p>
        ) : null}

        {!unavailable && item.priceChanged ? (
          <Badge tone="neutral" className="mt-2">
            Prix mis à jour depuis l&apos;ajout
          </Badge>
        ) : null}

        {!unavailable && item.stock.quantityAvailable > 0 && item.stock.quantityAvailable <= 5 ? (
          <p className="mt-1 text-xs text-muted">Plus que {item.stock.quantityAvailable} en stock</p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-4 sm:justify-end">
        {!unavailable ? (
          <div className="flex items-center border border-line">
            <button
              type="button"
              onClick={() =>
                item.quantity - 1 < 1 ? onRemove(item.variantId) : onSetQuantity(item.variantId, item.quantity - 1)
              }
              disabled={isMutating}
              aria-label="Diminuer la quantité"
              className="px-3 py-2 text-ink hover:bg-cream2 disabled:cursor-not-allowed disabled:opacity-40"
            >
              −
            </button>
            <span className="min-w-[2ch] px-2 text-center text-sm" aria-live="polite">
              {item.quantity}
            </span>
            <button
              type="button"
              onClick={() => onSetQuantity(item.variantId, item.quantity + 1)}
              disabled={atStockLimit || isMutating}
              aria-label="Augmenter la quantité"
              className="px-3 py-2 text-ink hover:bg-cream2 disabled:cursor-not-allowed disabled:opacity-40"
              title={atStockLimit ? "Quantité maximum disponible atteinte" : undefined}
            >
              +
            </button>
          </div>
        ) : null}

        <p className="w-24 text-right text-sm font-semibold text-ink">{formatXOF(item.lineTotal.amountMinor)}</p>

        <button
          type="button"
          onClick={() => onRemove(item.variantId)}
          disabled={isMutating}
          aria-label={`Retirer ${item.productName} du panier`}
          className="text-lg leading-none text-muted hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
        >
          ✕
        </button>
      </div>
    </Card>
  );
}
